(() => {
  const mounted = new WeakMap();
  const mount = (root) => {
    if (!root || mounted.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const variants = JSON.parse(root.querySelector('[data-variants]').textContent);
    const messages = JSON.parse(root.querySelector('[data-luma-messages]').textContent);
    const currency = root.dataset.currency || 'USD';
    const locale = root.dataset.locale || document.documentElement.lang || 'en';
    const money = (value) => new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value / 100);
    const select = root.querySelector('[data-variant-select]');
    const form = root.querySelector('.vf-product-form');
    const add = root.querySelector('[data-add]');
    const label = root.querySelector('[data-add-label]');
    const status = root.querySelector('[data-form-status]');
    const quantity = form.querySelector('[name="quantity"]');
    const gallery = root.querySelector('[data-gallery]');
    const slides = [...root.querySelectorAll('[data-slide]')];
    const thumbs = [...root.querySelectorAll('[data-gallery-index]')];
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const behavior = () => reduced.matches ? 'auto' : 'smooth';
    const currentVariant = () => variants.find((variant) => String(variant.id) === select.value);
    const normalizeQuantity = () => quantity.value = Math.max(1, Math.min(99, Math.round(Number(quantity.value) || 1)));
    let galleryFrame = 0;
    let pending = false;

    const syncVariant = (updateUrl = true) => {
      const variant = currentVariant();
      if (!variant) return;
      root.querySelector('[data-price]').textContent = `${money(variant.price)} ${currency}`;
      root.querySelector('[data-sticky-price]').textContent = `${money(variant.price)} ${currency}`;
      const compare = root.querySelector('[data-compare]');
      const saving = root.querySelector('[data-saving]');
      compare.hidden = !(variant.compare_at_price > variant.price);
      compare.textContent = money(variant.compare_at_price || 0);
      saving.hidden = compare.hidden;
      saving.textContent = `${messages.save} ${money(Math.max(0, (variant.compare_at_price || 0) - variant.price))}`;
      const target = slides.find((slide) =>
        String(slide.dataset.mediaId) === String(variant.featured_media?.id) ||
        String(slide.dataset.imageId) === String(variant.featured_image?.id));
      if (target) gallery.scrollTo({ left: slides.indexOf(target) * gallery.clientWidth, behavior: 'auto' });
      add.disabled = pending || !variant.available;
      label.textContent = pending ? messages.adding : variant.available ? messages.add : messages.soldout;
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    };

    const updateGallery = () => {
      galleryFrame = 0;
      const index = Math.max(0, Math.min(slides.length - 1, Math.round(gallery.scrollLeft / gallery.clientWidth)));
      thumbs.forEach((button, i) => {
        button.classList.toggle('is-active', i === index);
        button.setAttribute('aria-pressed', String(i === index));
      });
      root.querySelector('[data-gallery-current]').textContent = String(index + 1);
      const activeThumb = thumbs[index];
      if (activeThumb) {
        const rail = activeThumb.parentElement;
        if (activeThumb.offsetLeft < rail.scrollLeft || activeThumb.offsetLeft + activeThumb.offsetWidth > rail.scrollLeft + rail.clientWidth) {
          rail.scrollTo({ left: activeThumb.offsetLeft - rail.offsetLeft - rail.clientWidth / 2 + activeThumb.offsetWidth / 2, behavior: 'auto' });
        }
      }
    };
    select.addEventListener('change', () => syncVariant(), { signal });
    root.querySelector('[data-quantity-minus]').addEventListener('click', () => { quantity.value = Number(quantity.value || 1) - 1; normalizeQuantity(); }, { signal });
    root.querySelector('[data-quantity-plus]').addEventListener('click', () => { quantity.value = Number(quantity.value || 1) + 1; normalizeQuantity(); }, { signal });
    quantity.addEventListener('change', normalizeQuantity, { signal });
    thumbs.forEach((button, index) => button.addEventListener('click', () => gallery.scrollTo({ left: index * gallery.clientWidth, behavior: behavior() }), { signal }));
    gallery.addEventListener('scroll', () => { if (!galleryFrame) galleryFrame = requestAnimationFrame(updateGallery); }, { passive: true, signal });
    gallery.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      gallery.scrollBy({ left: gallery.clientWidth * (event.key === 'ArrowRight' ? 1 : -1), behavior: behavior() });
    }, { signal });

    const arrival = root.querySelector('[data-arrival-range]');
    const delivery = root.querySelector('[data-delivery]');
    const updateArrival = () => {
      if (!arrival || !delivery) return;
      const now = new Date();
      const minimum = Number(delivery.dataset.daysMin) || 7;
      const maximum = Math.max(minimum, Number(delivery.dataset.daysMax) || 10);
      const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() + minimum, 12);
      const until = new Date(now.getFullYear(), now.getMonth(), now.getDate() + maximum, 12);
      const formatter = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', ...(from.getFullYear() !== now.getFullYear() || until.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) });
      arrival.textContent = formatter.formatRange ? formatter.formatRange(from, until) : `${formatter.format(from)} – ${formatter.format(until)}`;
    };
    updateArrival();
    document.addEventListener('visibilitychange', () => { if (!document.hidden) updateArrival(); }, { signal });

    const sticky = root.querySelector('[data-sticky]');
    const observer = new IntersectionObserver((entries) => { sticky.hidden = entries[0].isIntersecting || window.scrollY < 300; }, { threshold: 0 });
    observer.observe(form);
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (pending || add.disabled || !currentVariant()?.available) return;
      normalizeQuantity();
      pending = true;
      syncVariant(false);
      status.textContent = '';
      const drawer = document.querySelector('cart-drawer');
      drawer?.setActiveElement?.(add);
      const data = new FormData(form);
      if (drawer?.getSectionsToRender) {
        data.append('sections', drawer.getSectionsToRender().map((section) => section.id).join(','));
        data.append('sections_url', window.location.pathname);
      }
      try {
        const route = window.routes?.cart_add_url || '/cart/add';
        const response = await fetch(route.endsWith('.js') ? route : `${route}.js`, {
          method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: data
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || messages.error);
        status.textContent = messages.added;
        if (drawer?.renderContents) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(result);
        } else window.location.assign(window.routes?.cart_url || '/cart');
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: result }));
      } catch (error) {
        status.textContent = error.message || messages.error;
      } finally {
        pending = false;
        syncVariant(false);
      }
    }, { signal });
    root.querySelector('.vf-cart-link')?.addEventListener('click', (event) => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.open) { event.preventDefault(); drawer.open(); }
    }, { signal });
    syncVariant(false);
    mounted.set(root, () => { controller.abort(); observer.disconnect(); cancelAnimationFrame(galleryFrame); mounted.delete(root); });
  };
  document.querySelectorAll('[data-luma-root]').forEach(mount);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-luma-root]').forEach(mount));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-luma-root]').forEach((root) => mounted.get(root)?.()));
  // Reordered story sections keep policy and question links on the product page.
  if (!document.documentElement.dataset.lumaAnchors) {
    document.documentElement.dataset.lumaAnchors = 'true';
    document.addEventListener('click', (event) => {
      const link = event.target.closest('.vf-luma a[href^="#vf-"]');
      if (!link) return;
      const target = document.getElementById(link.hash.slice(1));
      if (target?.tagName === 'DETAILS') target.open = true;
    });
  }
})();
